import 'whatwg-fetch';

import { withLDProvider } from 'launchdarkly-react-client-sdk';

import DatadogLogger from 'components/core/logging/DatadogLogger';
import { getLaunchDarklyPlugins } from 'components/core/logging/launchDarklyObservability';
import Router from 'components/core/routing/Router';
import { ANONYMOUS_USER_KEY } from 'utils/featureFlagUtils';

export default withLDProvider({
  clientSideID: process.env['REACT_APP_LAUNCH_DARKLY_CLIENT_ID'] || '',
  options: {
    /*
     * Forward every LaunchDarkly flag evaluation to Datadog RUM (@feature_flags.<key>),
     * so RUM errors/latency can be sliced by flag state. Using LD's `flag-used` inspector
     * captures all evaluations centrally — no per-hook instrumentation. DatadogLogger gates
     * on RUM being enabled, so this is a no-op in dev where RUM is off.
     */
    inspectors: [
      {
        method: (flagKey, flagDetail) => DatadogLogger.addFeatureFlagEvaluation(flagKey, flagDetail.value),
        name: 'datadog-rum-feature-flags',
        type: 'flag-used',
      },
    ],
    /*
     * LaunchDarkly's own observability plugin. It reports errors, logs and traces to LD
     * with the evaluated flags attached, which is what lets an error be traced back to the
     * flag that gated the code path — the Datadog inspector above only goes the other way.
     * Empty outside deployed environments; see launchDarklyObservability.
     */
    plugins: getLaunchDarklyPlugins(),
  },
  user: {
    anonymous: true,
    key: ANONYMOUS_USER_KEY,
  },
})(Router);

/**
 * Force repaint when app is visible to get rid of weird artifacts on screen e.g. black scrollbars.
 *
 * Repaint source:
 * https://stackoverflow.com/questions/3485365/how-can-i-force-webkit-to-redraw-repaint-to-propagate-style-changes
 */
document.addEventListener('visibilitychange', () => {
  const documentRoot = document.querySelector<HTMLDivElement>('#root');
  if (!documentRoot || !document.hidden) {
    return;
  }

  documentRoot.style.display = 'none';

  const _offsetHeight = documentRoot.offsetHeight; // Reference variable to take effect
  documentRoot.style.display = '';
});
